export const ASSIGNMENT_TRACKING_VERSION = 2;

export const ASSIGNMENT_TRACKING_SYNC_INTERVAL_MS = 90000;

const ASSIGNMENT_CLIENT_SESSION_STORAGE_KEY = 'owe_asg_client_session_id';

function createAssignmentClientSessionId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function getAssignmentClientSessionId() {
    try {
        let id = sessionStorage.getItem(ASSIGNMENT_CLIENT_SESSION_STORAGE_KEY);
        if (!id) {
            id = createAssignmentClientSessionId();
            sessionStorage.setItem(ASSIGNMENT_CLIENT_SESSION_STORAGE_KEY, id);
        }
        return id;
    } catch {
        return createAssignmentClientSessionId();
    }
}

export function rotateAssignmentClientSessionId() {
    const id = createAssignmentClientSessionId();
    try {
        sessionStorage.setItem(ASSIGNMENT_CLIENT_SESSION_STORAGE_KEY, id);
    } catch {
    }
    return id;
}

export const ASSIGNMENT_TRACK_CLIP_MAX = 480;

export const ASSIGNMENT_TRACK_SNIPPET_CAP = 10;

export function getAssignmentTrackingStorageKey(assignmentId, userId, classId, submissionId) {
    const sub = submissionId || 'new';
    return `owe_asg_track_${assignmentId}_${userId}_${classId}_${sub}`;
}

export function readAssignmentTrackingLocal(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const v = JSON.parse(raw);
        return v && typeof v === 'object' ? v : null;
    } catch {
        return null;
    }
}

export function writeAssignmentTrackingLocal(key, payload) {
    try {
        localStorage.setItem(key, JSON.stringify(payload));
    } catch {
    }
}

export function truncateAssignmentTrackText(text, maxLen = ASSIGNMENT_TRACK_CLIP_MAX) {
    if (!text || typeof text !== 'string') return '';
    const t = text.replace(/\s+/g, ' ').trim();
    if (!t) return '';
    return t.length <= maxLen ? t : `${t.slice(0, maxLen)}…`;
}

export function pushLimitedSnippet(list, text, cap = ASSIGNMENT_TRACK_SNIPPET_CAP) {
    if (!text) return list;
    const next = [...list, text];
    while (next.length > cap) next.shift();
    return next;
}

export function mergeNumericField(serverVal, localVal, memVal) {
    const a = Number(serverVal) || 0;
    const b = Number(localVal) || 0;
    const c = Number(memVal) || 0;
    return Math.max(a, b, c);
}

export function mergeSnippetLists(serverList, localList, cap = ASSIGNMENT_TRACK_SNIPPET_CAP) {
    const s = Array.isArray(serverList) ? serverList : [];
    const l = Array.isArray(localList) ? localList : [];
    const merged = [...s, ...l].map((x) => (typeof x === 'string' ? x : '')).filter(Boolean);
    return merged.slice(-cap);
}

export function getServerMetaDataFingerprint(serverMeta) {
    if (!serverMeta || typeof serverMeta !== 'object') {
        return '';
    }
    const s = serverMeta;
    const copySnips = Array.isArray(s.copy_content_snippets) ? s.copy_content_snippets : [];
    const pasteSnips = Array.isArray(s.paste_content_snippets) ? s.paste_content_snippets : [];
    const parts = [
        s.tracking_version,
        s.duration_sec,
        s.resume_count,
        s.finish_later_count,
        s.page_visibility_change_count,
        s.copy_attempt_count,
        s.paste_attempt_count,
        s.context_menu_attempt_count,
        s.retake_sessions_count,
        s.window_blur_count,
        s.assignment_session_started_at,
        copySnips.length,
        pasteSnips.length,
    ];
    return parts.map((v) => (v != null && v !== '' ? String(v) : '')).join('\x1e');
}

export function getRetakeInitSessionStorageKey(assignmentId, submissionId, submissionsCount) {
    return `owe_asg_retake_init_${assignmentId}_${submissionId}_${submissionsCount}`;
}

export function buildResetTrackingStateForRetakeAttempt(completedSubmissionsCount) {
    const n = Math.max(0, Math.floor(Number(completedSubmissionsCount) || 0));
    return {
        duration_sec: 0,
        resume_count: 0,
        finish_later_count: 0,
        page_visibility_change_count: 0,
        copy_attempt_count: 0,
        paste_attempt_count: 0,
        copy_content_snippets: [],
        paste_content_snippets: [],
        context_menu_attempt_count: 0,
        window_blur_count: 0,
        retake_sessions_count: n,
        assignment_session_started_at: new Date().toISOString(),
    };
}

export function buildInitialTrackingState(serverMeta, localPersisted) {
    const server = serverMeta && typeof serverMeta === 'object' ? serverMeta : {};
    const rawLocal = localPersisted && typeof localPersisted === 'object' ? localPersisted : {};
    const local = { ...rawLocal };
    delete local.retake_track_submissions;
    return {
        duration_sec: mergeNumericField(server.duration_sec, local.duration_sec, 0),
        resume_count: mergeNumericField(server.resume_count, local.resume_count, 0),
        finish_later_count: mergeNumericField(server.finish_later_count, local.finish_later_count, 0),
        page_visibility_change_count: mergeNumericField(
            server.page_visibility_change_count,
            local.page_visibility_change_count,
            0
        ),
        copy_attempt_count: mergeNumericField(server.copy_attempt_count, local.copy_attempt_count, 0),
        paste_attempt_count: mergeNumericField(server.paste_attempt_count, local.paste_attempt_count, 0),
        copy_content_snippets: mergeSnippetLists(server.copy_content_snippets, local.copy_content_snippets),
        paste_content_snippets: mergeSnippetLists(server.paste_content_snippets, local.paste_content_snippets),
        context_menu_attempt_count: mergeNumericField(
            server.context_menu_attempt_count,
            local.context_menu_attempt_count,
            0
        ),
        retake_sessions_count: mergeNumericField(server.retake_sessions_count, local.retake_sessions_count, 0),
        window_blur_count: mergeNumericField(server.window_blur_count, local.window_blur_count, 0),
        assignment_session_started_at:
            server.assignment_session_started_at ||
            local.assignment_session_started_at ||
            new Date().toISOString(),
    };
}

export function snapshotMetaDataForApi(state) {
    const ua = typeof navigator !== 'undefined' && navigator.userAgent ? String(navigator.userAgent) : '';
    return {
        tracking_version: ASSIGNMENT_TRACKING_VERSION,
        duration_sec: Math.max(0, Math.floor(Number(state.duration_sec) || 0)),
        resume_count: Math.max(0, Math.floor(Number(state.resume_count) || 0)),
        finish_later_count: Math.max(0, Math.floor(Number(state.finish_later_count) || 0)),
        page_visibility_change_count: Math.max(0, Math.floor(Number(state.page_visibility_change_count) || 0)),
        copy_attempt_count: Math.max(0, Math.floor(Number(state.copy_attempt_count) || 0)),
        paste_attempt_count: Math.max(0, Math.floor(Number(state.paste_attempt_count) || 0)),
        copy_content_snippets: Array.isArray(state.copy_content_snippets)
            ? state.copy_content_snippets.slice(-ASSIGNMENT_TRACK_SNIPPET_CAP)
            : [],
        paste_content_snippets: Array.isArray(state.paste_content_snippets)
            ? state.paste_content_snippets.slice(-ASSIGNMENT_TRACK_SNIPPET_CAP)
            : [],
        context_menu_attempt_count: Math.max(0, Math.floor(Number(state.context_menu_attempt_count) || 0)),
        retake_sessions_count: Math.max(0, Math.floor(Number(state.retake_sessions_count) || 0)),
        window_blur_count: Math.max(0, Math.floor(Number(state.window_blur_count) || 0)),
        assignment_session_started_at: state.assignment_session_started_at || null,
        client_session_id: getAssignmentClientSessionId(),
        client_user_agent: ua,
    };
}

export function getResumeOnceSessionStorageKey(assignmentId) {
    return `owe_asg_resume_once_${assignmentId}`;
}

export function getReplayResumeOnceSessionStorageKey(assignmentId) {
    return `owe_asg_replay_resume_once_${assignmentId}`;
}

export function clearAssignmentTrackingLocalForAssignment(assignmentId, userId, classId) {
    if (!assignmentId || !userId || !classId) return;
    const prefix = `owe_asg_track_${assignmentId}_${userId}_${classId}_`;
    try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (key && key.startsWith(prefix)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
    }
}
